import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { flow, get, sortBy } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { connect } from 'react-redux';
import { TrackingHandler } from 'client/tracking/handler';
import { ComparatorMap } from 'client/tracking/maps/comparator/comparator';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildPopularStylesPath,
  buildVehiclePath,
  FeatureSpecsPaths,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import { TRIMS_SUB_NAV_SECTIONS_LIST } from 'site-modules/comparator/constants/sections-map';
import { DEALERLESS_OEM } from 'site-modules/shared/constants/oem';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { isUsed } from 'site-modules/shared/utils/publication-states';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { ComparatorAnchorNav } from 'site-modules/comparator/components/comparator-anchor-nav/comparator-anchor-nav';
import { ZipWithDefaultActionHandling } from 'site-modules/shared/components/zip-with-default-action-handling/zip-with-default-action-handling';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { FloodlightAd } from 'site-modules/shared/components/floodlight/floodlight-ad';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { TrimsComparatorGrid } from 'site-modules/comparator/components/trims-comparator-grid/trims-comparator-grid';
import { TrimPricingBarsFooterProgressive } from 'site-modules/shared/components/core-page/trim-pricing-bars/trim-pricing-bars-footer/trim-pricing-bars-footer-progressive';
import { TrimsComparatorIntro } from 'site-modules/comparator/components/trims-comparator-intro/trims-comparator-intro';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';

function TrimsComparatorUI({
  params: paramsProp,
  vehicle = null,
  styles = null,
  visitorLocation = null,
  pageName = null,
  styleAttributes = null,
  isMobile = false,
}) {
  const [customOffset, setCustomOffset] = useState(0);
  const adjustCustomOffset = useDebounce(newCustomOffset => setCustomOffset(newCustomOffset), 500);

  useEffect(() => {
    TrackingHandler.useMap(ComparatorMap);
  }, []);

  if (!styles) {
    return null;
  }

  const {
    model: { name: modelName },
    submodels: { slug: submodelSlug },
    year,
    pubStates,
  } = vehicle;
  const isUsedPubState = isUsed(pubStates);
  const { city, stateCode } = visitorLocation;
  const isInternational = isUnknownZip(visitorLocation);
  const params = {
    ...paramsProp,
    isUsed: isUsedPubState,
    isElectric: !!styleAttributes?.electric,
    isPluginHybrid: !!styleAttributes?.pluginElectric,
    isDealerlessOem: DEALERLESS_OEM.includes(paramsProp.make),
  };
  const vehicleParams = { ...params, submodel: submodelSlug };
  const title = `${buildDisplayVehicle({
    makeModelSubmodelYear: vehicle,
    params,
    usedPrefix: '',
    updateChevroletMakeName: true,
    disableTrademarkCharacter: true,
  })} Trims Comparison`;
  const navList = TRIMS_SUB_NAV_SECTIONS_LIST[isUsedPubState].filter(
    ({ isEvItem }) => !isEvItem || params.isElectric || params.isPluginHybrid
  );

  return (
    <main id="main-content" tabIndex={-1} className="trims-comparator mb-3" data-tracking-parent="trim-comparator">
      <TrimsComparatorIntro
        params={params}
        vehicleParams={vehicleParams}
        vehicle={vehicle}
        visitorLocation={visitorLocation}
        title={title}
        isMobile={isMobile}
      />
      <Container>
        <h2 className="heading-3 mb-0_75">
          Compare {year} {modelName} trims side-by-side
        </h2>
        <ComparatorAnchorNav customOffset={customOffset} list={navList} ariaLabel="Comparison table" />

        <Row className="pb-1_25 pt-1 bg-cool-gray-90 ps-0_5">
          <Col xs="auto" className="gx-0 pe-0_25">
            Comparing average pricing near
          </Col>
          <Col xs="auto" className="gx-0">
            {city}, {stateCode} <ZipWithDefaultActionHandling buttonClassName="size-16 fw-normal" />
          </Col>
        </Row>
      </Container>
      <TrimsComparatorGrid
        vehicleParams={vehicleParams}
        title={title}
        styles={styles}
        navList={navList}
        isMobile={isMobile}
        setCustomOffset={adjustCustomOffset}
        customOffset={customOffset}
      />

      <FloodlightAd />
      <Container className="mt-1">
        <Experiment name="ads-12460_dfp" showDefault>
          <Recipe name="ctrl" isDefault>
            <MinHeightWrapper componentType="SPONSORED_CARS">
              <SponsoredCars />
            </MinHeightWrapper>
          </Recipe>
          <Recipe name="chal">
            <MinHeightWrapper componentType="SPONSORED_CARS_STACKED" style={isMobile ? null : { maxWidth: '744px' }}>
              <SponsoredCars showStackedAds />
            </MinHeightWrapper>
          </Recipe>
        </Experiment>
      </Container>

      {!isUsedPubState && (
        <Container>
          <Row>
            <Col xs={12} lg={9}>
              <TrimPricingBarsFooterProgressive
                params={params}
                isInternational={isInternational}
                dataTrackingParent={`new-pricing_module-${pageName}`}
              />
            </Col>
          </Row>
        </Container>
      )}

      <Container>
        <PageLinksWidget divideSections={false} title="Related information" hasTopDivider />
      </Container>
    </main>
  );
}

TrimsComparatorUI.propTypes = {
  params: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  styles: PropTypes.arrayOf(VehicleEntities.Style),
  visitorLocation: VisitorEntities.Location,
  styleAttributes: PropTypes.objectOf(PropTypes.bool),
  pageName: PropTypes.string,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: get(state, 'mobile'),
  visitorLocation: get(state, 'visitor.location'),
  pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const TrimsComparator = flow(
  component =>
    connectToModel(component, {
      styles: bindToPath(
        ({ vehicle }) => !!vehicle && buildPopularStylesPath(getParamsFromVehicle(vehicle)),
        VehicleModel,
        styles => sortBy(styles, 'price.baseMSRP')
      ),
      styleAttributes: bindToPath(
        ({ vehicle }) => !!vehicle && FeatureSpecsPaths.buildPartialFeaturesPath(getParamsFromVehicle(vehicle)),
        VehicleModel,
        styles => styles?.[0]?.styleAttributes
      ),
    }),
  component =>
    connectToModel(component, {
      vehicle: bindToPath(({ params }) => buildVehiclePath(params), VehicleModel),
    })
)(connect(mapStateToProps)(TrimsComparatorUI));
